<template>
  <PrimaryButton
    @click="
      onClick(
        props.localRecordingId,
        props.recordingName,
        props.aiTemplateId,
        props.integrationData
      )
    "
    :loading="isLoading"
  >
    <slot />
  </PrimaryButton>
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { Network } from '@capacitor/network';
import { SendError } from '@/helpers.js';
import PrimaryButton from '@/components/brand/PrimaryButton.vue';
import { Sentry } from '@/vendors/Sentry';

const $store = useStore();

let isLoading = ref(false);

const props = defineProps({
  localRecordingId: {
    type: String,
    required: true,
  },
  recordingName: {
    type: String,
  },
  aiTemplateId: {
    type: String,
  },
  integrationData: {
    type: Object,
    required: false,
    default: () => ({
      integration: null,
      jobId: null,
      visitId: null,
    }),
  },
});

const emit = defineEmits(['onQueued', 'recordUploadingFailed']);

const onClick = async (localRecordingId, name, _templateId, integrationData) => {
  isLoading.value = true;
  try {
    const localRecording = await $store.getters['recs/local/getLocalRecording'](localRecordingId);

    const recordingName = name || localRecording.name || 'Untitled';
    if (!recordingName) {
      return SendError('Recording name is required');
    }
    const aiTemplateId = _templateId || localRecording.aiTemplateId;

    const integrationPayload =
      integrationData.visitId && integrationData.jobId
        ? integrationData.integration
          ? integrationData
          : { ...integrationData, integration: 'jobber' }
        : null;

    await $store.dispatch('recs/local/editLocalRecording', {
      id: localRecording.id,
      name: recordingName,
      aiTemplateId: aiTemplateId,
      integration: integrationPayload,
    });

    const { connected } = await Network.getStatus();
    if (!connected) {
      SendError('No internet connection, upload file when connected');
      return emit('recordUploadingFailed');
    }

    await $store.dispatch('recs/queueUploadRecording', { localRecordingId: localRecording.id });

    emit('onQueued');
  } catch (err) {
    Sentry.captureException(err);
    emit('recordUploadingFailed');
    SendError('Uploading failed, please try again');
  } finally {
    isLoading.value = false;
  }
};
</script>
